import React, { useEffect } from 'react'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import { useWatch, useController } from 'react-hook-form'
import { getUuid, transformDateInput } from 'src/Libs/flow/utils'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { TrashIcon } from 'src/Libs/flow/icons'
import { MyPvIo } from 'src/Libs/flow/installation/forms'
import { useInstallationForm } from 'src/Libs/flow/installation/hooks'
import { getTimeAsDate, isDateXBeforeDateY } from 'src/Libs/flow/installation/utils'
import { SettingsBox } from 'src/Elements/Data'
import { NumberField, TimePicker } from 'src/Libs/inputs/react-hook-form'
import { Day } from '@e3dc-react/shell/Libs/date/types'
import WeekDaySelector from 'src/Elements/Selectors/WeekDaySelector'
import { transformAbbreviatedToWeekdays, transformWeekdaysToAbbreviated } from '../../utils/weekday-transformer'
import { Alert } from '@e3dc-react/shell/Elements/Alert'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'

type Weekdays = NonNullable<MyPvIo>['boostConfig'][number]['weekDays']

export const BoostConfigSchedule: React.FC<{
  index: number
  remove: () => void
  errorWeekdays: Weekdays | undefined
  conflictingScheduleIndex: number | null | undefined
}> = ({ index, remove, errorWeekdays, conflictingScheduleIndex }) => {
  const { t } = useCustomTranslation()
  const { hasInstallationWritingRights: canEdit } = useInstallationAccessRightsContext()
  const { control, setError, clearErrors } = useInstallationForm()

  const { field } = useController({
    control,
    name: `ioInterfaces.mypv.boostConfig.${index}.weekDays`,
  })

  const schedule = useWatch({ control, name: `ioInterfaces.mypv.boostConfig.${index}` })

  const toIsBeforeFrom = isDateXBeforeDateY(schedule.toTime, schedule.fromTime)
  const hasScheduleConflict =
    conflictingScheduleIndex !== null && conflictingScheduleIndex !== undefined && !!errorWeekdays && Object.values(errorWeekdays).some(day => day)
  const noWeekdaySelected = Object.values(schedule.weekDays).every(day => day === false)

  const hasError = toIsBeforeFrom || hasScheduleConflict || noWeekdaySelected
  useEffect(() => {
    if (hasError) {
      setError(`ioInterfaces.mypv.boostConfig.${index}`, {
        type: 'schedule-error',
      })
    } else {
      clearErrors(`ioInterfaces.mypv.boostConfig.${index}`)
    }
  }, [hasError, setError, clearErrors, index])

  const handleDaySelect = (dayName: Day, selected: boolean): void => {
    const updatedDays = transformAbbreviatedToWeekdays({
      ...transformWeekdaysToAbbreviated(field.value),
      [dayName]: selected,
    })
    field.onChange({
      ...field.value,
      ...updatedDays,
    })
  }

  const disabledDays = (): Day[] => {
    if (!errorWeekdays) return []

    const abbreviatedDays = transformWeekdaysToAbbreviated(errorWeekdays)

    return Object.entries(abbreviatedDays)
      .filter(([_, hasError]) => hasError)
      .map(([dayName]) => dayName as Day)
  }

  return (
    <Box>
      <Stack direction={'row'} alignItems={'center'}>
        <Typography component="h4" fontWeight={'bold'} sx={{ color: hasError ? theme => theme.palette.error.main : undefined }}>
          {t('installation.ioInterfaceEditor.mypv.securityTemperature')} {index + 1}
        </Typography>
        <IconButton size="large" onClick={remove} sx={{ visibility: canEdit ? undefined : 'hidden' }}>
          <TrashIcon />
        </IconButton>
      </Stack>
      <SettingsBox
        title="generalSettings.staticTariffs.weekDays"
        suffix={
          <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
            <WeekDaySelector
              days={transformWeekdaysToAbbreviated(schedule.weekDays)}
              onSelectDay={handleDaySelect}
              disabledDays={disabledDays()}
              disabled={!canEdit}
            />
            {noWeekdaySelected && <Alert severity="error" text={t('installation.tariff.missingDay')} />}
          </Box>
        }
      />
      <SettingsBox
        title="installation.ioInterfaceEditor.myPv.requestedTemperature"
        suffix={
          <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
            <NumberField
              name={`ioInterfaces.mypv.boostConfig.${index}.securityTemperature`}
              label={t('installation.ioInterfaceEditor.myPv.requestedTemperature')}
              fullWidth
              min={0}
              max={90}
              disabled={!canEdit}
            />
          </Box>
        }
      />

      <SettingsBox
        title="common.from"
        suffix={
          <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
            <TimePicker
              name={`ioInterfaces.mypv.boostConfig.${index}.fromTime`}
              label={t('common.from')}
              onTransform={transformDateInput}
              fullWidth
              disabled={!canEdit}
            />
          </Box>
        }
      />
      <SettingsBox
        title="common.to"
        suffix={
          <Box sx={{ minWidth: { xs: '100%', sm: 'auto' } }}>
            <TimePicker
              name={`ioInterfaces.mypv.boostConfig.${index}.toTime`}
              label={t('common.to')}
              onTransform={transformDateInput}
              fullWidth
              disabled={!canEdit}
            />
          </Box>
        }
      />
      {toIsBeforeFrom && <Alert severity="error" text={t('installation.ioInterfaceEditor.myPv.from-more-than-to-error')} />}
      {hasScheduleConflict && (
        <Alert
          severity="error"
          text={t('installation.ioInterfaceEditor.myPv.schedule-conflict-error', {
            value: conflictingScheduleIndex + 1,
          })}
        />
      )}
    </Box>
  )
}

export const createDefaultBoostConfig = (): NonNullable<MyPvIo>['boostConfig'][number] => {
  return {
    id: getUuid(),
    fromTime: getTimeAsDate('06:00'),
    toTime: getTimeAsDate('17:00'),
    weekDays: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
    securityTemperature: 0,
  }
}
